import React, { useState, useEffect } from 'react';
import WorkItemBoard from './WorkItemBoard';
import WorkItemModal from './WorkItemModal';
import './Planner.css';
import { useAuth } from '../../../../context/AuthContext';

// API base URL based on environment
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL || 'https://api.veridicus.com.br'
  : process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:5000';

// Helper to get auth header
const getAuthHeader = () => {
  return {
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  };
};

const PlannerPage = () => {
  const { userData } = useAuth();
  const [workItems, setWorkItems] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [tiposCliente, setTiposCliente] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedWorkItem, setSelectedWorkItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [filterCliente, setFilterCliente] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);

  // Fetch work items
  const fetchWorkItems = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/work-items/`, {
        headers: getAuthHeader()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const itemsArray = data?.data || data;
      const validItems = Array.isArray(itemsArray) ? itemsArray : [];
      
      setWorkItems(validItems);
      setError(null);
    } catch (err) {
      setError('Erro ao carregar itens de trabalho: ' + err.message);
      console.error('Erro ao buscar work items:', err);
    }
  };

  // Fetch clientes
  const fetchClientes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/clientes/`, {
        headers: getAuthHeader()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const clientesArray = data?.clientes || data?.data || data;
      const validClientes = Array.isArray(clientesArray) ? clientesArray : [];
      
      setClientes(validClientes);
    } catch (err) {
      console.error('Erro ao buscar clientes:', err);
    }
  };

  // Fetch tipos de cliente
  const fetchTiposCliente = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tipos-cliente/`, {
        headers: getAuthHeader()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const tiposArray = data?.data || data;
      const validTipos = Array.isArray(tiposArray) ? tiposArray : [];
      
      setTiposCliente(validTipos.filter(tipo => tipo.ativo));
    } catch (err) {
      console.error('Erro ao buscar tipos de cliente:', err);
    }
  };

  // Fetch users for assignment
  const fetchUsers = async () => {
    try {
      // Check if user has admin permissions
      if (userData?.isAdmin || userData?.permissions?.includes('Master') || userData?.permissions?.includes('Diretoria')) {
        const response = await fetch(`${API_BASE_URL}/api/users/`, {
          headers: getAuthHeader()
        });
        
        if (response.ok) {
          const data = await response.json();
          const usersArray = data?.data || data?.users || data;
          const validUsers = Array.isArray(usersArray) ? usersArray : [];
          setUsers(validUsers);
        }
      }
    } catch (err) {
      console.error('Erro ao buscar usuários:', err);
    }
  };

  // Check if user can assign work items
  const canAssignWorkItem = (workItem) => {
    if (!userData) return false;
    
    // Check if user has Master or Diretoria permissions
    if (userData.permissions?.includes('Master') || userData.permissions?.includes('Diretoria')) {
      return true;
    }
    
    // Check if user is the creator of the work item
    if (workItem) {
      const creatorId = typeof workItem.createdBy === 'string' ? workItem.createdBy : workItem.createdBy?._id;
      return creatorId === userData._id;
    }
    
    return false;
  };

  // Check if user can delete work items
  const canDeleteWorkItem = () => {
    if (!userData) return false;
    
    // Only Master or Diretoria can delete
    return userData.permissions?.includes('Master') || userData.permissions?.includes('Diretoria');
  };

  // Initial data fetch
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchWorkItems(),
        fetchClientes(),
        fetchTiposCliente()
      ]);
      setLoading(false);
    };
    
    loadData();
  }, []);

  // Fetch users when userData is available
  useEffect(() => {
    if (userData) {
      fetchUsers();
    }
  }, [userData]);

  // Handle creating new work item
  const handleAddNew = () => {
    setSelectedWorkItem(null);
    setIsEditing(false);
    setShowForm(true);
    setIsModalOpen(true);
  };

  // Handle editing work item
  const handleEdit = (workItem) => {
    // Check if item is payment locked
    if (workItem.paymentLocked) {
      alert('Este item está bloqueado pois já recebeu pagamento. Apenas comentários podem ser adicionados.');
      return;
    }
    
    setSelectedWorkItem(workItem);
    setIsEditing(true);
    setShowForm(true);
    setIsModalOpen(true);
  };

  // Handle viewing work item details
  const handleView = (workItem) => {
    setSelectedWorkItem(workItem);
    setShowForm(false);
    setIsModalOpen(true);
  };

  // Handle status change (drag and drop)
  const handleStatusChange = async (itemId, newStatus) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/work-items/${itemId}/status`, {
        method: 'PUT',
        headers: getAuthHeader(),
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Update local state
      setWorkItems(prev => prev.map(item => 
        item._id === itemId ? { ...item, status: newStatus } : item
      ));
    } catch (err) {
      setError('Erro ao atualizar status: ' + err.message);
      console.error('Erro ao atualizar status:', err);
    }
  };

  // Handle quick completion update
  const handleQuickCompletionUpdate = async (workItemId, updateData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/work-items/${workItemId}`, {
        method: 'PUT',
        headers: getAuthHeader(),
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const updatedItem = await response.json();
      
      // Update local state for immediate UI feedback
      setWorkItems(prev => prev.map(item => 
        item._id === workItemId ? { ...updatedItem.data || updatedItem } : item
      ));
      
      // Update selected item if it's the one being updated
      if (selectedWorkItem && selectedWorkItem._id === workItemId) {
        setSelectedWorkItem(updatedItem.data || updatedItem);
      }
    } catch (err) {
      setError('Erro ao atualizar conclusão: ' + err.message);
      console.error('Erro ao atualizar conclusão:', err);
      alert('Erro ao atualizar conclusão: ' + err.message);
    }
  };

  // Handle form submit
  const handleFormSubmit = async (workItemData) => {
    try {
      const url = isEditing
        ? `${API_BASE_URL}/api/work-items/${selectedWorkItem._id}`
        : `${API_BASE_URL}/api/work-items`;
      
      const method = isEditing ? 'PUT' : 'POST';
      
      // Add user data to the work item
      if (!isEditing) {
        workItemData.createdBy = userData._id;
      }
      
      const response = await fetch(url, {
        method,
        headers: getAuthHeader(),
        body: JSON.stringify(workItemData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      // Refresh the list
      fetchWorkItems();
      
      // Close the modal
      setIsModalOpen(false);
      setShowForm(false);
    } catch (err) {
      setError('Erro ao salvar item de trabalho: ' + err.message);
      console.error('Erro ao salvar work item:', err);
      alert(err.message);
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este item de trabalho?')) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/work-items/${id}`, {
          headers: getAuthHeader(),
          method: 'DELETE',
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Refresh the list
        fetchWorkItems();
        
        // Close modal if the deleted item was selected
        if (selectedWorkItem && selectedWorkItem._id === id) {
          setIsModalOpen(false);
        }
      } catch (err) {
        setError('Erro ao excluir item de trabalho: ' + err.message);
        console.error('Erro ao excluir work item:', err);
      }
    }
  };

  // Filter work items
  const filteredWorkItems = workItems.filter(item => {
    // Filter by cliente - check both clienteId and clienteData._id
    if (filterCliente) {
      const itemClienteId = item.clienteId?._id || item.clienteId || item.clienteData?._id;
      if (itemClienteId !== filterCliente) return false;
    }
    
    // Filter by status
    if (filterStatus !== 'all' && item.status !== filterStatus) return false;
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        item.title?.toLowerCase().includes(term) ||
        item.description?.toLowerCase().includes(term) ||
        item.clienteData?.nome?.toLowerCase().includes(term)
      );
    }
    
    return true;
  });

  // Calculate totals by status
  const calculateTotals = () => {
    const totals = {
      todo: 0,
      inProgress: 0,
      done: 0
    };
    
    filteredWorkItems.forEach(item => {
      if (totals.hasOwnProperty(item.status)) {
        totals[item.status] += item.totalValue || 0;
      }
    });
    
    return totals;
  };

  const totals = calculateTotals();

  return (
    <div className="planner-container">
      <div className="planner-header">
        <h1>Planejamento de Trabalho</h1>
        <button className="planner-btn planner-btn-primary" onClick={handleAddNew}>
          + Novo Item de Trabalho
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="planner-filters">
        <div className="filter-group">
          <label>Buscar:</label>
          <input
            type="text"
            className="filter-input"
            placeholder="Buscar por título, descrição ou cliente..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filter-group">
          <label>Cliente:</label>
          <select
            className="filter-select"
            value={filterCliente}
            onChange={(e) => setFilterCliente(e.target.value)}
          >
            <option value="">Todos os Clientes</option>
            {clientes.map(cliente => (
              <option key={cliente._id} value={cliente._id}>
                {cliente.nomeFantasia || cliente.razaoSocial || ''}
              </option>
            ))}
          </select>
        </div>
        
        <div className="filter-group">
          <label>Status:</label>
          <select
            className="filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">Todos</option>
            <option value="todo">A Fazer</option>
            <option value="inProgress">Em Progresso</option>
            <option value="done">Concluído</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">Carregando...</div>
      ) : (
        <WorkItemBoard
          workItems={filteredWorkItems}
          onStatusChange={handleStatusChange}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          totals={totals}
          canDelete={canDeleteWorkItem()}
        />
      )}

      <WorkItemModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setShowForm(false);
        }}
        workItem={selectedWorkItem}
        showForm={showForm}
        isEditing={isEditing}
        onEdit={() => handleEdit(selectedWorkItem)}
        onDelete={() => handleDelete(selectedWorkItem._id)}
        onSubmit={handleFormSubmit}
        onQuickUpdate={handleQuickCompletionUpdate}
        clientes={clientes}
        tiposCliente={tiposCliente}
        users={users}
        canAssign={canAssignWorkItem(selectedWorkItem)}
        canDelete={canDeleteWorkItem()}
        userData={userData}
      />
    </div>
  );
};

export default PlannerPage;
